import type Phaser from 'phaser';

import { opaquePixels } from './upgrade-shine.js';

/**
 * What sets a wandering rare (ADR 0094) apart from its kin, whose body it
 * wears: a one-pixel outline in the rares' jade round the whole creature,
 * breathing, with a bright arc running round it. Not an elite's colour, not
 * a legendary piece's fire: a player who has seen one knows the next.
 * Redrawn on a single Graphics each tick, on whole pixels.
 */

/** The rares' own colour: their name, their outline, their mark on the minimap. */
export const RARE_COLOUR = 0x3ce8b4;
/** Its light, where the arc passes. */
const RARE_LIGHT = 0xd8fff0;

const TICK_MS = 90;
/** Ticks for the outline to breathe in and out, and for the arc to go round. */
const BREATH_TICKS = 22;
const ARC_LAP = 26;

type Pixel = readonly [number, number];

export class RareMark {
  /** In front of the creature's layers. */
  readonly front: Phaser.GameObjects.Container;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly timer: Phaser.Time.TimerEvent;
  private ticks = Math.floor(Math.random() * BREATH_TICKS);

  constructor(
    scene: Phaser.Scene,
    private readonly layers: () => Iterable<Phaser.GameObjects.Sprite>,
  ) {
    this.front = scene.add.container(0, 0);
    this.graphics = scene.add.graphics();
    this.front.add(this.graphics);
    this.timer = scene.time.addEvent({
      delay: TICK_MS,
      loop: true,
      callback: () => {
        this.tick();
      },
    });
  }

  setVisible(visible: boolean): void {
    this.front.setVisible(visible);
  }

  destroy(): void {
    this.timer.remove(false);
    this.front.destroy(true);
  }

  private tick(): void {
    this.ticks += 1;
    this.graphics.clear();
    if (!this.front.visible) return;
    const body = new Set<number>();
    const pixels: Pixel[] = [];
    for (const sprite of this.layers()) {
      if (!sprite.visible) continue;
      const left = Math.round(sprite.x - sprite.displayOriginX);
      const top = Math.round(sprite.y - sprite.displayOriginY);
      for (const [x, y] of opaquePixels(sprite.frame)) {
        const cell = key(left + x, top + y);
        if (body.has(cell)) continue;
        body.add(cell);
        pixels.push([left + x, top + y]);
      }
    }
    const ring = outlineOf(pixels, body);
    if (ring.length === 0) return;
    let cx = 0;
    let cy = 0;
    for (const [x, y] of ring) {
      cx += x;
      cy += y;
    }
    cx /= ring.length;
    cy /= ring.length;
    const breath = 0.6 + 0.4 * Math.sin((2 * Math.PI * this.ticks) / BREATH_TICKS);
    const turn = (2 * Math.PI * this.ticks) / ARC_LAP;
    for (const [x, y] of ring) {
      // One arc, a quarter of the way round, lit over the breathing outline.
      const wave = Math.cos(Math.atan2(y - cy, x - cx) - turn);
      if (wave > 0.7) this.graphics.fillStyle(RARE_LIGHT, 1);
      else this.graphics.fillStyle(RARE_COLOUR, breath);
      this.graphics.fillRect(x, y, 1, 1);
    }
  }
}

const NEIGHBOURS: readonly Pixel[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** Empty pixels next to the body, once each. */
function outlineOf(pixels: readonly Pixel[], covered: ReadonlySet<number>): Pixel[] {
  const done = new Set<number>();
  const ring: Pixel[] = [];
  for (const [x, y] of pixels) {
    for (const [dx, dy] of NEIGHBOURS) {
      const cell = key(x + dx, y + dy);
      if (covered.has(cell) || done.has(cell)) continue;
      done.add(cell);
      ring.push([x + dx, y + dy]);
    }
  }
  return ring;
}

function key(x: number, y: number): number {
  return (x + 2048) * 4096 + (y + 2048);
}
